import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelCustomerUsersBatchJob,
  getCustomerUsersBatchFailures,
  getCustomerUsersBatchList,
  uploadCustomerUsersBatchFile,
} from "controller/api";
import type { ApiErrorType } from "shared";
import { sentryLogger } from "utilz";

export const useUploadCustomerUserBatchFile = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      customerId,
      file,
      sendInvite,
    }: {
      customerId: string;
      file: File;
      sendInvite: boolean;
    }) => uploadCustomerUsersBatchFile({ customerId, file, sendInvite }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customer_user_batch"]);
      },
      onError: (error: ApiErrorType, variables) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "uploadCustomerUserBatchFile",
        });
      },
    },
  );
};

export const useGetCustomerUserBatchFailuresByMutation = () => {
  return useMutation({
    mutationFn: (jobId: string) => getCustomerUsersBatchFailures(jobId),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "customer",
        level: "error",
        functionName: "getCustomerUserBatchFailures",
      });
    },
  });
};

export const useGetCustomerUserBatchList = ({
  limit = 100,
  offset = 0,
}: {
  limit?: number;
  offset?: number;
}) => {
  return useQuery({
    queryKey: ["customer_user_batch", limit, offset],
    queryFn: () => getCustomerUsersBatchList({ limit, offset }),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "customer",
        level: "error",
        functionName: "getCustomerUserBatchList",
      });
    },
  });
};

export const useCancelCustomerUserJob = () => {
  const queryClient = useQueryClient();
  return useMutation((jobId: string) => cancelCustomerUsersBatchJob(jobId), {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["customer_user_batch_status", variables]);
      queryClient.invalidateQueries(["customer_user_batch"]);
    },
    onError: (error: ApiErrorType, variables) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "customer",
        level: "error",
        functionName: "cancelCustomerUserJob",
      });
    },
  });
};
